import React, { useEffect, useState } from 'react';
import { Button, Form, Header, Segment, Divider, Label, Message } from 'semantic-ui-react';
import { useParams, useNavigate } from 'react-router-dom';
import { API, showError, showSuccess } from '../../helpers';
import { renderQuota, renderQuotaWithPrompt } from '../../helpers/render';

const EditUser = () => {
  const params = useParams();
  const userId = params.id;
  const [loading, setLoading] = useState(true);
  const [inputs, setInputs] = useState({
    username: '',
    display_name: '',
    password: '',
    github_id: '',
    wechat_id: '',
    email: '',
    quota: 0,
    group: 'default'
  });
  const [groupOptions, setGroupOptions] = useState([]);

  // Action 配额相关状态
  const [actionData, setActionData] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionInputs, setActionInputs] = useState({ total_actions: '', daily_limit: '' });

  const { username, display_name, password, github_id, wechat_id, email, quota, group } = inputs;

  const handleInputChange = (e, { name, value }) => {
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  };

  const handleActionInputChange = (e, { name, value }) => {
    setActionInputs((prev) => ({ ...prev, [name]: value }));
  };

  const fetchGroups = async () => {
    try {
      let res = await API.get(`/api/group/`);
      setGroupOptions(res.data.data.map((group) => ({
        key: group,
        text: group,
        value: group,
      })));
    } catch (error) {
      showError(error.message);
    }
  };

  const navigate = useNavigate();
  const handleCancel = () => {
    navigate("/setting");
  };

  const loadUser = async () => {
    let res = undefined;
    if (userId) {
      res = await API.get(`/api/user/${userId}`);
    } else {
      res = await API.get(`/api/user/self`);
    }
    const { success, message, data } = res.data;
    if (success) {
      data.password = '';
      setInputs(data);
    } else {
      showError(message);
    }
    setLoading(false);
  };

  const loadActionData = async () => {
    if (!userId) return;
    setActionLoading(true);
    try {
      const res = await API.get(`/api/user/${userId}/actions`);
      const data = res.data;
      setActionData(data);
      setActionInputs({
        total_actions: String(data.total_actions ?? ''),
        daily_limit: String(data.daily_limit ?? ''),
      });
    } catch (e) {
      // 用户可能还没有 action 记录，静默忽略
    }
    setActionLoading(false);
  };

  useEffect(() => {
    loadUser().then();
    if (userId) {
      fetchGroups().then();
      loadActionData().then();
    }
  }, []);

  const submit = async () => {
    let res = undefined;
    if (userId) {
      let data = { ...inputs, id: userId };
      if (typeof data.quota === 'string') {
        data.quota = parseInt(data.quota);
      }
      res = await API.put(`/api/user/`, data);
    } else {
      res = await API.put(`/api/user/self`, inputs);
    }
    const { success, message } = res.data;
    if (success) {
      showSuccess('用户信息更新成功！');
    } else {
      showError(message);
    }
  };

  const submitActionUpdate = async () => {
    const body = {};
    if (actionInputs.total_actions !== '') body.total_actions = parseInt(actionInputs.total_actions);
    if (actionInputs.daily_limit !== '') body.daily_limit = parseInt(actionInputs.daily_limit);
    if (Object.keys(body).length === 0) return;
    const res = await API.put(`/api/user/${userId}/actions`, body);
    const d = res.data;
    if (d.success) {
      showSuccess('Action 配额更新成功！');
      await loadActionData();
    } else {
      showError(d.error || '更新失败');
    }
  };

  const submitResetDaily = async () => {
    const res = await API.post(`/api/user/${userId}/actions/reset-daily`, {});
    const d = res.data;
    if (d.success) {
      showSuccess('今日用量已重置！');
      await loadActionData();
    } else {
      showError(d.error || '重置失败');
    }
  };

  const renderActionInfo = () => {
    if (!actionData) return <Message info>暂无 Action 配额记录</Message>;
    const remaining = actionData.total_actions - actionData.used_actions;
    const dailyRemaining = actionData.daily_limit > 0
      ? actionData.daily_limit - actionData.daily_used
      : null;
    const planColors = { FREE: 'grey', BASIC: 'blue', PRO: 'purple', ENTERPRISE: 'gold' };
    return (
      <div style={{ marginBottom: '1em' }}>
        <Label.Group>
          <Label>订阅计划 <Label.Detail>{actionData.subscription_plan || 'FREE'}</Label.Detail></Label>
          <Label color={remaining >= 0 ? 'green' : 'red'}>
            总剩余 <Label.Detail>{remaining} / {actionData.total_actions}</Label.Detail>
          </Label>
          <Label>今日已用 <Label.Detail>{actionData.daily_used}</Label.Detail></Label>
          {actionData.daily_limit > 0
            ? <Label color={dailyRemaining >= 0 ? 'teal' : 'orange'}>今日剩余 <Label.Detail>{dailyRemaining} / {actionData.daily_limit}</Label.Detail></Label>
            : <Label color='teal'>每日限额 <Label.Detail>无限制</Label.Detail></Label>
          }
        </Label.Group>
      </div>
    );
  };

  return (
    <>
      <Segment loading={loading}>
        <Header as='h3'>更新用户信息</Header>
        <Form autoComplete='new-password'>
          <Form.Field>
            <Form.Input
              label='用户名'
              name='username'
              placeholder={'请输入新的用户名'}
              onChange={handleInputChange}
              value={username}
              autoComplete='new-password'
            />
          </Form.Field>
          <Form.Field>
            <Form.Input
              label='密码'
              name='password'
              type={'password'}
              placeholder={'请输入新的密码，最短 8 位'}
              onChange={handleInputChange}
              value={password}
              autoComplete='new-password'
            />
          </Form.Field>
          <Form.Field>
            <Form.Input
              label='显示名称'
              name='display_name'
              placeholder={'请输入新的显示名称'}
              onChange={handleInputChange}
              value={display_name}
              autoComplete='new-password'
            />
          </Form.Field>
          {
            userId && <>
              <Form.Field>
                <Form.Dropdown
                  label='分组'
                  placeholder={'请选择分组'}
                  name='group'
                  fluid
                  search
                  selection
                  allowAdditions
                  additionLabel={'请在系统设置页面编辑分组倍率以添加新的分组：'}
                  onChange={handleInputChange}
                  value={inputs.group}
                  autoComplete='new-password'
                  options={groupOptions}
                />
              </Form.Field>
              <Form.Field>
                <Form.Input
                  label={`剩余额度${renderQuotaWithPrompt(quota)}`}
                  name='quota'
                  placeholder={'请输入新的剩余额度'}
                  onChange={handleInputChange}
                  value={quota}
                  type={'number'}
                  autoComplete='new-password'
                />
              </Form.Field>
            </>
          }
          <Form.Field>
            <Form.Input
              label='已绑定的 GitHub 账户'
              name='github_id'
              value={github_id}
              autoComplete='new-password'
              placeholder='此项只读，需要用户通过个人设置页面的相关绑定按钮进行绑定，不可直接修改'
              readOnly
            />
          </Form.Field>
          <Form.Field>
            <Form.Input
              label='已绑定的微信账户'
              name='wechat_id'
              value={wechat_id}
              autoComplete='new-password'
              placeholder='此项只读，需要用户通过个人设置页面的相关绑定按钮进行绑定，不可直接修改'
              readOnly
            />
          </Form.Field>
          <Form.Field>
            <Form.Input
              label='已绑定的邮箱账户'
              name='email'
              value={email}
              autoComplete='new-password'
              placeholder='此项只读，需要用户通过个人设置页面的相关绑定按钮进行绑定，不可直接修改'
              readOnly
            />
          </Form.Field>
          <Button onClick={handleCancel}>取消</Button>
          <Button positive onClick={submit}>提交</Button>
        </Form>
      </Segment>

      {userId && (
        <Segment loading={actionLoading}>
          <Header as='h3'>Action 配额管理</Header>
          {renderActionInfo()}
          <Form>
            <Form.Group widths={3}>
              <Form.Input
                label='总 Actions 数量'
                name='total_actions'
                type='number'
                min='0'
                placeholder='留空则不修改'
                value={actionInputs.total_actions}
                onChange={handleActionInputChange}
              />
              <Form.Input
                label='每日限额（0 = 不限制）'
                name='daily_limit'
                type='number'
                min='0'
                placeholder='留空则不修改'
                value={actionInputs.daily_limit}
                onChange={handleActionInputChange}
              />
            </Form.Group>
            <Button primary onClick={submitActionUpdate}>保存 Action 配额</Button>
            <Button color='orange' onClick={submitResetDaily}>重置今日用量</Button>
          </Form>
        </Segment>
      )}
    </>
  );
};

export default EditUser;
